import PageLoader from '@components/PageLoader'
import { useAdminUsers } from '@hooks/useAdminUsers'
import { useAdminFilter } from '@pages/Admin/AdminFilterContext'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AdminHeading } from '../AdminLayout.styles'
import ListStats from '../ListStats'
import {
	DeletedTag,
	InternalTag,
	PageButton,
	Pagination,
	PageStatus,
	Row,
	SearchInput,
	Table,
	TableWrap,
	Td,
	Th,
	Toolbar,
} from './UsersPage.styles'
import { EmptyNote } from '@components/EmptyState'
import { ErrorNote } from '@components/Notice'

const PAGE_SIZE = 25

/** Unix seconds → a short, locale-formatted signup date. */
const formatSignupDate = (unixSeconds: number): string =>
	new Date(unixSeconds * 1000).toLocaleDateString(undefined, {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	})

/**
 * Admin users list: a searchable, server-paginated table of every account
 * (soft-deleted ones flagged), scoped to the global creation-date range from the
 * admin layout's filter. Search is debounced and any change — search or date
 * range — resets to the first page so the view never lands on an empty page.
 */
const UsersPage: React.FC = () => {
	const navigate = useNavigate()
	const { from, to } = useAdminFilter()
	const [searchInput, setSearchInput] = useState('')
	const [debouncedQ, setDebouncedQ] = useState('')
	const [page, setPage] = useState(1)

	// Debounce the search box so we refetch on a pause, not on every keystroke.
	useEffect(() => {
		const timer = setTimeout(() => setDebouncedQ(searchInput.trim()), 300)
		return () => clearTimeout(timer)
	}, [searchInput])

	// Any new filter (search term or date range) starts the result set over at page 1.
	useEffect(() => {
		setPage(1)
	}, [debouncedQ, from, to])

	const { data, isLoading, isError } = useAdminUsers({ page, pageSize: PAGE_SIZE, q: debouncedQ })

	return (
		<>
			<AdminHeading>Users</AdminHeading>

			<Toolbar>
				<SearchInput
					type="search"
					aria-label="Search users by name or email"
					placeholder="Search by name or email…"
					value={searchInput}
					onChange={e => setSearchInput(e.target.value)}
				/>
			</Toolbar>

			{isLoading ? (
				<PageLoader label="Loading users…" />
			) : isError || !data ? (
				<ErrorNote>Could not load users. Please try again.</ErrorNote>
			) : (
				<>
					<ListStats noun="users" total={data.totalAllTime} newInRange={data.total} />

					<TableWrap>
						<Table>
							<thead>
								<tr>
									<Th>Name</Th>
									<Th>Email</Th>
									<Th>Signup date</Th>
									<Th>Trips</Th>
								</tr>
							</thead>
							<tbody>
								{data.users.length === 0 ? (
									<tr>
										<Td colSpan={4}>
											<EmptyNote>No users match this filter.</EmptyNote>
										</Td>
									</tr>
								) : (
									data.users.map(user => (
										<Row
											key={user.id}
											$deleted={user.isDeleted}
											onClick={() => navigate(`/admin/users/${user.id}`)}
										>
											<Td>
												{user.firstName} {user.lastName}
												{user.isInternal && <InternalTag>Internal</InternalTag>}
												{user.isDeleted && <DeletedTag>Deleted</DeletedTag>}
											</Td>
											<Td>{user.email}</Td>
											<Td>{formatSignupDate(user.signupDate)}</Td>
											<Td>{user.tripCount.toLocaleString()}</Td>
										</Row>
									))
								)}
							</tbody>
						</Table>
					</TableWrap>

					<Pagination>
						<PageStatus>
							Page {data.page} of {data.totalPages} · {data.total.toLocaleString()} users
						</PageStatus>
						<PageButton disabled={data.page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
							Previous
						</PageButton>
						<PageButton
							disabled={data.page >= data.totalPages}
							onClick={() => setPage(p => p + 1)}
						>
							Next
						</PageButton>
					</Pagination>
				</>
			)}
		</>
	)
}

export default UsersPage
